/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  ListItem,
  OrderedList,
  PasswordInput,
  TextInput
} from 'carbon-components-react';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { ChannelContentProps } from '../../../util/types';
import { X_WATSON_GENESYS_VERIFICATION_TOKEN } from '../constants';
import { links } from '../links';
import { FIELDS } from '../schema';
import { GenesysSMSAuthMessages as messages } from './messages';

export const GenesysAuth = ({
  isQuickSetup,
  readOnly
}: ChannelContentProps) => {
  const formMethods = useFormContext();
  const { formatMessage: t } = useIntl();
  const { registerInputField } = useFormHelpers(formMethods);

  const clientIdChangeState = useState<boolean>(false);
  const clientSecretChangeState = useState<boolean>(false);
  const verificationTokenChangeState = useState<boolean>(false);
  const apiUriChangeState = useState<boolean>(false);

  return (
    <div className="TabContent__field-section">
      <OrderedList className="TabContent__subtitle GenesysAuth">
        <ListItem>
          <FormattedMessage
            values={{
              clientId: <strong>{t(messages.clientId)}</strong>,
              clientSecret: <strong>{t(messages.clientSecret)}</strong>
            }}
            {...messages.stepOne}
          />
          <div className="TabContent__spacer16" />
          <PasswordInput
            {...registerInputField({
              field: FIELDS.GENESYS_CLIENT_ID,
              isPasswordField: true,
              isQuickSetup,
              labelText: messages.clientId,
              placeholderText: messages.clientId,
              changeState: clientIdChangeState,
              readOnly
            })}
            id="genesyssms-sms-client-id"
          />
          <div className="TabContent__spacer16" />
          <PasswordInput
            {...registerInputField({
              field: FIELDS.GENESYS_CLIENT_SECRET,
              isPasswordField: true,
              isQuickSetup,
              labelText: messages.clientSecret,
              placeholderText: messages.clientSecret,
              changeState: clientSecretChangeState,
              readOnly
            })}
            id="genesys-sms-client-secret"
          />
        </ListItem>
        <div className="TabContent__spacer16" />
        <ListItem>
          <FormattedMessage
            values={{
              xWatsonGenesysVerificationToken: (
                <strong>{X_WATSON_GENESYS_VERIFICATION_TOKEN}</strong>
              ),
              webServicesActionData: (
                <strong>{t(messages.webServicesActionData)}</strong>
              )
            }}
            {...messages.stepTwo}
          />
          <div className="TabContent__spacer16" />
          <PasswordInput
            {...registerInputField({
              field: FIELDS.GENESYS_VERIFICATION_TOKEN,
              isPasswordField: true,
              isQuickSetup,
              labelText: messages.verificationToken,
              placeholderText: messages.verificationToken,
              changeState: verificationTokenChangeState,
              readOnly
            })}
            id="genesys-sms-verification-token"
          />
        </ListItem>
        <div className="TabContent__spacer16" />
        <ListItem>
          <FormattedMessageWithLink
            url={links.genesysAPILink}
            {...messages.stepThree}
          />
          <div className="TabContent__spacer16" />
          <TextInput
            {...registerInputField({
              field: FIELDS.PROVIDER_URL,
              isPasswordField: false,
              isQuickSetup,
              labelText: messages.apiUriLabel,
              placeholderText: messages.apiUriPlaceHolder,
              changeState: apiUriChangeState,
              readOnly
            })}
            id="genesys-sms-api-uri"
          />
        </ListItem>
      </OrderedList>
    </div>
  );
};
